import React, { useState } from 'react';
import { EditIcon, VideoIcon, DownloadIcon, Tag as TagIcon, ChevronDownIcon, ChevronUpIcon } from '../icons/Icons';
import { getImageExtFromUrl } from '../../utils/imageFormat';

interface ImageTag {
    id: number;
    name: string;
    color: string;
}

interface ImageVariation {
    id: number;
    url: string;
    thumbnailUrl?: string;
    width?: number;
    height?: number;
    createdAt: string;
}

interface GroupedImageCardProps {
    id: string;
    src: string;
    thumbnailUrl?: string;
    width?: number;
    height?: number;
    variations: ImageVariation[];
    variationsCount: number;
    tags?: ImageTag[];
    onEditItem: (item: any) => void;
    onStartVideoCreation: (item: any) => void;
    onDownload: (id: string, filename: string) => void;
    onImageClick: (imageId: string) => void;
    onTagManage: (item: any) => void;
}

const GroupedImageCard: React.FC<GroupedImageCardProps> = ({
    id,
    src,
    thumbnailUrl,
    width,
    height,
    variations,
    variationsCount,
    tags,
    onEditItem,
    onStartVideoCreation,
    onDownload,
    onImageClick,
    onTagManage,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const parentItem = {
        id,
        type: 'image' as const,
        src,
        thumbnailUrl,
        width,
        height,
        tags,
    };

    return (
        <div className="relative">
            {/* Main Parent Card */}
            <div className="group relative">
                {/* Stacked card effect - only show when collapsed and has variations */}
                {!isExpanded && variationsCount > 0 && (
                    <>
                        <div className="absolute -bottom-1 left-1 right-1 h-full bg-snapfy-inner/50 rounded-xl -z-10" />
                        <div className="absolute -bottom-2 left-2 right-2 h-full bg-snapfy-inner/30 rounded-xl -z-20" />
                    </>
                )}

                {/* Parent Image */}
                <div className="relative">
                    <img
                        src={thumbnailUrl || src}
                        alt="Imagem original"
                        loading="lazy"
                        decoding="async"
                        className="w-full aspect-square object-cover rounded-xl cursor-pointer bg-snapfy-inner"
                        onClick={() => onImageClick(id)}
                    />

                    {/* Variations Badge */}
                    {variationsCount > 0 && (
                        <button
                            onClick={handleToggleExpand}
                            className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-[#7063f7]/90 backdrop-blur-sm text-white text-xs font-medium rounded-full hover:bg-[#7063f7] transition-colors z-10"
                        >
                            <span>+{variationsCount} {variationsCount === 1 ? 'edição' : 'edições'}</span>
                            {isExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                        </button>
                    )}

                    {/* Dimensions Badge */}
                    {width && height && (
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <span className="text-[10px] text-white/80 bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded">
                                {width}×{height}
                            </span>
                        </div>
                    )}

                    {/* Tags Overlay */}
                    {tags && tags.length > 0 && (
                        <div className="absolute top-2 left-2 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                            {tags.map(tag => (
                                <span
                                    key={tag.id}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white shadow-sm"
                                    style={{ backgroundColor: tag.color }}
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Hover Actions */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 rounded-lg flex flex-col items-center justify-center p-4 text-center pointer-events-none">
                        <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDownload(id, `snapfy-gallery-${id}.${getImageExtFromUrl(src)}`);
                                }}
                                className="p-2 bg-black/50 rounded-full text-white hover:bg-[#7063f7] focus:opacity-100"
                                title="Baixar Imagem"
                            >
                                <DownloadIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditItem(parentItem);
                                }}
                                className="p-2 bg-black/50 rounded-full text-white hover:bg-[#7063f7] focus:opacity-100"
                                title="Edição Rápida"
                            >
                                <EditIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStartVideoCreation(parentItem);
                                }}
                                className="p-2 bg-black/50 rounded-full text-white hover:bg-[#7063f7] focus:opacity-100"
                                title="Criar Vídeo"
                            >
                                <VideoIcon className="w-5 h-5" />
                            </button>
                            <button
                                className="p-2 bg-black/50 rounded-full text-white hover:bg-[#7063f7] focus:opacity-100"
                                title="Gerenciar Tags"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTagManage(parentItem);
                                }}
                            >
                                <TagIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Variations Grid */}
            {isExpanded && variations.length > 0 && (
                <div className="mt-2 pl-4 border-l-2 border-[#7063f7]/50">
                    <div className="text-xs text-snapfy-text-secondary mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#7063f7] rounded-full" />
                        Versões editadas
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {variations.map((variation, index) => {
                            const variationItem = {
                                id: `img_${variation.id}`,
                                type: 'image' as const,
                                src: variation.url,
                                thumbnailUrl: variation.thumbnailUrl,
                                width: variation.width,
                                height: variation.height,
                            };

                            // Calculate order: variations are sorted desc, so reverse the index
                            const orderNumber = variations.length - index;

                            return (
                                <div key={variation.id} className="group relative">
                                    <img
                                        src={variation.thumbnailUrl || variation.url}
                                        alt={`Versão ${orderNumber}`}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full aspect-square object-cover rounded-xl cursor-pointer bg-snapfy-inner border border-[#7063f7]/30"
                                        onClick={() => onImageClick(`img_${variation.id}`)}
                                    />

                                    {/* Hover Actions for Variations */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDownload(`img_${variation.id}`, `snapfy-edit-${variation.id}.${getImageExtFromUrl(variation.url)}`);
                                                }}
                                                className="p-1.5 bg-black/50 rounded-full text-white hover:bg-[#7063f7]"
                                                title="Baixar"
                                            >
                                                <DownloadIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEditItem(variationItem);
                                                }}
                                                className="p-1.5 bg-black/50 rounded-full text-white hover:bg-[#7063f7]"
                                                title="Editar"
                                            >
                                                <EditIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Generation order badge */}
                                    <div className="absolute bottom-1 right-1 text-[10px] font-medium text-white bg-[#7063f7]/80 px-1.5 py-0.5 rounded">
                                        v{orderNumber}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupedImageCard;
